"use client";

import { createContext, type RefObject } from "react";

import type { AuthIdentityModel, ProviderMismatchError } from "../types";

export type AuthUnauthorizedType = "invalid" | "missing";

export type AuthContextState = {
  /**
   * The identity token
   **/
  identityToken: string | null;
  /**
   * The login URL
   **/
  loginURL: string;
  /**
   * The forced login URL
   * @deprecated
   **/
  forcedLoginURL: string;
  /**
   * The identity of the user
   **/
  indentity: AuthIdentityModel | null;
  /**
   * Ref to check if a token refresh is currently in progress.
   * Using a ref instead of state ensures the current value is always
   * accessed, avoiding stale closures in async callbacks.
   **/
  isRefreshingRef: RefObject<boolean | null>;
  /**
   * Whether the user has become unauthorized and a redirect to the login
   * page is in progress. Consumers should keep showing a loading state
   * (instead of an error/content) while this is true, so the app doesn't
   * flash an error screen before the browser navigates away.
   **/
  isUnauthorized: boolean;
  /**
   * When the user is unauthorized
   **/
  unauthorized: (type: AuthUnauthorizedType) => void;
  /**
   * Logout the user
   **/
  logout: () => void;
  /**
   * Handle provider mismatch error by logging out the user.
   * This occurs when user's account is for a different provider than the system.
   **/
  handleProviderMismatch: (error: ProviderMismatchError) => void;
  /**
   * Initialize the middleware
   **/
  initializeMiddleware: () => void;
};

export const AuthContext = createContext<AuthContextState | undefined>(
  undefined,
);
